// Transitional English translations for interface copy emitted outside React
// screens: hooks, API/service fallbacks, shared configuration/model labels,
// Live Tracking, and application-owned email/PDF shells.
//
// This catalog must never contain user-entered values, backend payload data,
// company/person names, or AI prompts. Dynamic rules translate only a known
// application-owned shell and preserve every captured business value verbatim.

const translateRecipient = (recipient) => ({
  expeditor: 'sender',
  Expeditor: 'Sender',
  destinatar: 'consignee',
  Destinatar: 'Consignee',
}[recipient] || recipient);

const translateFeatureLabel = (label) => ({
  'Management transporturi': 'Transport management',
  'Echipaj transport': 'Transport crew',
  'Management flotă': 'Fleet management',
  'Management personal': 'Workforce management',
  Administrare: 'Administration',
  'Planificare rută': 'Route planning',
  'Urmărire live': 'Live tracking',
  'Asistent AI': 'AI assistant',
  Profitabilitate: 'Profitability',
  'Casă de expediție': 'Freight forwarding',
  'Notificări WhatsApp': 'WhatsApp notifications',
  'Cotații transport': 'Transport quotations',
}[label] || label);

const translateGeneratedFieldList = (labels) => {
  const translations = {
    'Nume Expeditor': 'Sender name',
    'Nume Destinatar': 'Consignee name',
    'Loc Livrare': 'Delivery location',
    'Număr Înmatriculare': 'Registration number',
    'Număr VIN': 'VIN',
    Marcă: 'Make',
    Model: 'Model',
    'An Fabricație': 'Year of manufacture',
    'Tip Remorcă': 'Trailer type',
  };
  return labels.split(', ').map((label) => translations[label] || label).join(', ');
};

const translateGeneratedCmrEmail = (message) => message
  .replaceAll('📋 DOCUMENT CMR', '📋 CMR DOCUMENT')
  .replaceAll('📷 FOTOGRAFIE CMR', '📷 CMR PHOTO')
  .replaceAll('Bună ziua,', 'Hello,')
  .replaceAll('Vă transmitem documentul CMR pentru transportul organizat.', 'Please find attached the CMR document for the arranged transport.')
  .replaceAll('Vă transmitem fotografia documentului CMR pentru transportul organizat.', 'Please find attached a photo of the CMR document for the arranged transport.')
  .replaceAll('🚛 DETALII TRANSPORT:', '🚛 TRANSPORT DETAILS:')
  .replaceAll('📋 Cod Transport:', '📋 Transport code:')
  .replaceAll('🏢 Companie:', '🏢 Company:')
  .replaceAll('🚛 Șofer:', '🚛 Driver:')
  .replaceAll('📍 LOCAȚII:', '📍 LOCATIONS:')
  .replaceAll('📍 Loc preluare:', '📍 Pickup location:')
  .replaceAll('📍 Loc predare:', '📍 Delivery location:')
  .replaceAll('🏢 EXPEDITOR:', '🏢 SENDER:')
  .replaceAll('🎯 DESTINATAR:', '🎯 CONSIGNEE:')
  .replaceAll('🏢 Nume:', '🏢 Name:')
  .replaceAll('📍 Adresa:', '📍 Address:')
  .replaceAll('🌍 Țara:', '🌍 Country:')
  .replaceAll('📦 INFORMAȚII MARFĂ:', '📦 CARGO DETAILS:')
  .replaceAll('📦 Natura mărfii:', '📦 Description of goods:')
  .replaceAll('⚖️ Greutate brută:', '⚖️ Gross weight:')
  .replaceAll('📏 Volum:', '📏 Volume:')
  .replaceAll('📊 Nr. colete:', '📊 Number of packages:')
  .replaceAll('📷 Documentul atașat reprezintă fotografia CMR-ului fizic scanat/fotografiat.', '📷 The attachment is a scan or photo of the physical CMR document.')
  .replaceAll('Vă rugăm să verificați documentul atașat și să ne contactați pentru orice clarificări.', 'Please review the attached document and contact us if you need any clarification.')
  .replaceAll('🌐 Interfață AtruX:', '🌐 AtruX interface:')
  .replaceAll('Cu stimă,', 'Kind regards,')
  .replaceAll('📧 Email generat automat - Nu răspundeți la acest mesaj', '📧 Automatically generated email — please do not reply');

export const legacyEnglishSystem = Object.freeze({
  // Live Tracking
  'Fara locatie': 'No location',
  'Inactiv / neactualizat': 'Inactive / not updated',
  'Pe drum': 'On the road',
  'In pauza': 'On break',
  'Vezi transportul': 'View transport',
  'Soferi pe harta (': 'Drivers on the map (',
  'activi •': 'active •',
  'inactivi': 'inactive',
  '• Pe drum': '• On the road',
  '• In pauza': '• On break',
  'Se incarca transportul...': 'Loading transport...',

  // AI analysis and chat runtime copy (prompts sent to the model are excluded)
  'A apărut o eroare la analiza documentelor.': 'An error occurred while analysing the documents.',
  'A apărut o eroare la analiza CMR-ului.': 'An error occurred while analysing the CMR.',
  'A apărut o eroare la analiza fotografiilor.': 'An error occurred while analysing the photos.',
  'Eroare la extragerea datelor din CMR': 'Error extracting data from the CMR',
  'Eroare la extragerea datelor din documente': 'Error extracting data from the documents',
  'Eroare la verificarea documentelor': 'Error verifying the documents',
  'Sunt necesare exact 2 documente pentru comparație': 'Exactly two documents are required for comparison',
  'Eroare la compararea documentelor': 'Error comparing the documents',
  'Eroare la evaluarea daunelor': 'Error assessing the damage',
  'Te rugăm să introduci un mesaj sau să selectezi un fișier.': 'Enter a message or select a file.',
  'Răspuns primit.': 'Response received.',
  'A apărut o eroare la trimiterea mesajului.': 'An error occurred while sending the message.',
  'Fișierul este prea mare. Dimensiunea maximă: 10MB': 'The file is too large. Maximum size: 10 MB',
  'Fișierul este prea mare. Dimensiunea maximă: 10MB.': 'The file is too large. Maximum size: 10 MB.',
  'Tip de fișier neacceptat. Folosește imagini (JPG, PNG, GIF) sau PDF.': 'Unsupported file type. Use an image (JPG, PNG or GIF) or a PDF.',
  'Bună! Sunt AI Asistentul AtruX': 'Hello! I am the AtruX AI Assistant',
  'Cu ce te pot ajuta astăzi?': 'How can I help you today?',
  'Verific transporturile și șoferii disponibili': 'Check transports and available drivers',
  'Asignez șoferi la transporturi': 'Assign drivers to transports',
  'Ofer statistici și rapoarte': 'Provide statistics and reports',
  'Identific riscuri și probleme potențiale': 'Identify risks and potential issues',
  'Execut acțiuni cu confirmare': 'Carry out actions after confirmation',
  'Poți încărca imagini sau PDF-uri pentru analiză': 'You can upload images or PDFs for analysis',
  'Scrie "da" sau apasă Confirmă pentru a executa acțiuni': 'Type “yes” or select Confirm to carry out actions',
  'Folosește Shift+Enter pentru linie nouă în mesaj': 'Use Shift+Enter to start a new line',
  'A apărut o eroare la adăugarea șoferului. Vă rugăm încercați din nou.': 'An error occurred while adding the driver. Please try again.',
  'Eroare la încărcarea șoferilor': 'Error loading drivers',
  'Analiza AI a fost oprită în această pagină.': 'AI analysis has been disabled on this page.',
  'Cel puțin un fișier este necesar.': 'At least one file is required.',
  'Autentificare necesară pentru a accesa această funcționalitate.': 'Sign-in is required to use this feature.',
  'Analiza AI nu a putut fi verificată. Încearcă din nou.': 'The AI analysis could not be checked. Try again.',
  'Eroare de conexiune. Te rugăm să verifici conexiunea la internet și să încerci din nou.': 'Connection error. Check your internet connection and try again.',
  'Analiza AI a răspuns într-un format neașteptat. Încearcă din nou.': 'The AI analysis returned an unexpected response. Try again.',
  'Este necesar cel puțin un fișier pentru analiză.': 'At least one file is required for analysis.',
  'Unul dintre fișiere nu este valid.': 'One of the files is invalid.',
  'Format de răspuns invalid.': 'Invalid response format.',
  'Nivel de detaliu invalid.': 'Invalid level of detail.',
  'Ai depășit limita de cereri. Încearcă din nou mai târziu.': 'You have exceeded the request limit. Try again later.',
  'Serviciul de analiză nu este disponibil momentan.': 'The analysis service is currently unavailable.',
  'Configurație invalidă. Contactează suportul.': 'Invalid configuration. Contact support.',
  'Prea multe cereri către serviciul de analiză. Încearcă din nou în câteva minute.': 'Too many requests to the analysis service. Try again in a few minutes.',
  'Analiza a durat prea mult. Te rugăm să încerci cu fișiere mai mici.': 'The analysis took too long. Try again with smaller files.',
  'A apărut o eroare neașteptată.': 'An unexpected error occurred.',
  'Autentificare necesară pentru a utiliza chat-ul AI': 'Sign-in is required to use AI chat',
  'Mesajul nu poate fi gol': 'The message cannot be empty',
  'Eroare de conexiune. Verifică conexiunea la internet.': 'Connection error. Check your internet connection.',
  'Autentificare necesară pentru a utiliza analiza AI': 'Sign-in is required to use AI analysis',
  'Fișierul este necesar pentru analiză': 'A file is required for analysis',
  'Ai atins limita de cereri. Te rugăm să aștepți câteva minute.': 'You have reached the request limit. Wait a few minutes.',
  'AI-ul durează prea mult să răspundă. Te rugăm să încerci din nou.': 'The AI service is taking too long to respond. Try again.',
  'Nu se poate conecta la serviciul AI. Verifică conexiunea.': 'Could not connect to the AI service. Check your connection.',
  'Date invalide. Verifică informațiile introduse.': 'Invalid data. Check the information you entered.',
  'Sesiune expirată. Te rugăm să te autentifici din nou.': 'Your session has expired. Sign in again.',
  'Nu ai permisiuni pentru această acțiune.': 'You do not have permission to perform this action.',
  'Tip de fișier invalid. Folosește imagini sau PDF.': 'Invalid file type. Use an image or PDF.',
  'Mesajul este necesar.': 'A message is required.',
  'Eroare de server. Te rugăm să încerci mai târziu.': 'Server error. Try again later.',
  'confirmă': 'confirm',
  'anulează': 'cancel',
  'Eroare la generarea documentului. Vă rugăm încercați din nou.': 'Error generating the document. Please try again.',

  // Authentication, access and session fallbacks
  'Te rog să introduci codul': 'Enter the code',
  'Codul temporar trebuie să aibă între 8 și 16 caractere.': 'The temporary code must be between 8 and 16 characters long.',
  'Codul temporar nu este setat': 'The temporary code has not been set',
  'Cod sau tip de acces lipsesc': 'The access code or access type is missing',
  'Nu s-a putut determina transportul pentru acest cod': 'The transport associated with this code could not be determined',
  'Nu sunteți autentificat': 'You are not signed in',
  'Nu ești autentificat.': 'You are not signed in.',
  'Nu există token de autentificare.': 'No authentication token is available.',
  'Introduceți numărul de telefon': 'Enter your phone number',
  'Introduceți parola': 'Enter your password',
  'Parola trebuie să conțină cel puțin 6 caractere': 'The password must be at least 6 characters long',
  'A apărut o eroare la autentificare': 'An error occurred while signing in',
  'Numărul de telefon sau parola sunt incorecte': 'The phone number or password is incorrect',
  'Eroare de conexiune. Verificați internetul și încercați din nou.': 'Connection error. Check your internet connection and try again.',
  'Eroare de server. Încercați din nou în câteva momente.': 'Server error. Try again in a few moments.',
  'Introduceți un email valid pentru resetarea parolei': 'Enter a valid email address to reset your password',
  'Dacă există un cont cu acest email, instrucțiunile au fost trimise.': 'If an account exists for this email address, instructions have been sent.',
  'Eroare la resetarea parolei. Încercați din nou.': 'Error resetting the password. Please try again.',
  'Nu am reușit să te autentificăm. Verifică datele introduse și încearcă din nou.': 'We could not sign you in. Check your details and try again.',
  'Răspuns neașteptat de la server. Te rugăm să încerci din nou.': 'Unexpected response from the server. Try again.',
  'Există mai multe conturi active pentru acest număr.': 'There are multiple active accounts for this number.',
  'Prea multe încercări. Așteaptă un minut și încearcă din nou.': 'Too many attempts. Wait one minute and try again.',
  'Număr de telefon sau parolă incorectă.': 'Incorrect phone number or password.',
  'Date invalide.': 'Invalid data.',
  'Eroare la autentificare.': 'Sign-in error.',
  'Un cod de verificare a fost trimis pe email.': 'A verification code has been sent by email.',
  'Nu se poate conecta la server. Verifică conexiunea la internet.': 'Could not connect to the server. Check your internet connection.',
  'Ai greșit codul de 5 ori. Solicită un cod nou.': 'You entered the wrong code five times. Request a new code.',
  'Codul a expirat. Solicită un cod nou.': 'The code has expired. Request a new code.',
  'Sesiunea de verificare a expirat. Te rugăm să te autentifici din nou.': 'The verification session has expired. Sign in again.',
  'Eroare la verificarea codului.': 'Error verifying the code.',
  'Ai solicitat prea multe coduri. Așteaptă 15 minute.': 'You have requested too many codes. Wait 15 minutes.',
  'Prea multe cereri. Așteaptă un minut și încearcă din nou.': 'Too many requests. Wait one minute and try again.',
  'Sesiunea a expirat. Te rugăm să te autentifici din nou.': 'Your session has expired. Sign in again.',
  'Eroare la retrimiterea codului.': 'Error resending the code.',
  'Un nou cod a fost trimis pe email.': 'A new code has been sent by email.',
  'Eroare la deconectarea tuturor dispozitivelor.': 'Error signing out all devices.',
  'Eroare la obținerea sesiunilor.': 'Error retrieving sessions.',
  'Sesiunea nu a fost găsită.': 'Session not found.',
  'Nu poți revoca sesiunea curentă. Folosește Logout.': 'You cannot revoke the current session. Sign out instead.',
  'Eroare la revocarea sesiunii.': 'Error revoking the session.',
  'Eroare la obținerea statusului 2FA.': 'Error retrieving 2FA status.',
  'Nu poți activa 2FA fără o adresă de email validă.': 'You cannot enable 2FA without a valid email address.',
  'Parolă incorectă.': 'Incorrect password.',
  'Nu există o sesiune de verificare activă. Te rugăm să te autentifici din nou.': 'There is no active verification session. Sign in again.',
  'Cod invalid sau expirat': 'Invalid or expired code',
  'Cod invalid sau expirat. Te rog solicită un cod nou de la dispecer.': 'Invalid or expired code. Request a new code from the dispatcher.',
  'Acces blocat: codul a fost folosit deja de pe alt IP sau dispozitiv.': 'Access blocked: the code has already been used from another IP address or device.',
  'Nu s-au găsit informații pentru acest cod.': 'No information was found for this code.',
  'Codul a expirat sau a fost deja consumat. Solicită un cod nou.': 'The code has expired or has already been used. Request a new code.',
  'Prea multe încercări. Așteaptă și reîncearcă.': 'Too many attempts. Wait and try again.',
  'Serviciu temporar indisponibil. Reîncearcă în câteva minute.': 'The temporary access service is unavailable. Try again in a few minutes.',
  'ID-ul transportului lipsește': 'The transport ID is missing',
  'Sesiunea ta a expirat. Te rugăm să te autentifici din nou.': 'Your session has expired. Sign in again.',
  'Ai fost deconectat dintr-o altă fereastră.': 'You were signed out from another window.',
  'Acces temporar': 'Temporary access',
  DEZACTIVAT: 'DISABLED',
  'Beneficii:': 'Benefits:',

  // Cross-cutting validation, notifications and map fallbacks
  'Locație necunoscută': 'Unknown location',
  'API Key pentru Google Maps lipsește': 'The Google Maps API key is missing',
  'REACT_APP_GOOGLE_MAPS_API_KEY lipsește din configurația buildului': 'REACT_APP_GOOGLE_MAPS_API_KEY is missing from the build configuration',
  'Numărul de telefon este obligatoriu': 'Phone number is required',
  'Numărul de telefon este prea scurt': 'The phone number is too short',
  'Parola este obligatorie': 'Password is required',
  'Probleme de conexiune la internet. Verificați conexiunea și încercați din nou.': 'Internet connection problem. Check your connection and try again.',
  'Nu s-a putut conecta la server. Verificați conexiunea la internet.': 'Could not connect to the server. Check your internet connection.',
  'Sesiunea dvs. a expirat. Vă rugăm să vă autentificați din nou.': 'Your session has expired. Sign in again.',
  'Nu aveți permisiunea de a accesa această resursă.': 'You do not have permission to access this resource.',
  'Resursa solicitată nu a fost găsită.': 'The requested resource was not found.',
  'Eroare internă de server. Vă rugăm să încercați din nou mai târziu.': 'Internal server error. Please try again later.',
  'Operația a durat prea mult. Vă rugăm să încercați din nou.': 'The operation took too long. Please try again.',
  'Datele introduse nu sunt valide. Verificați și încercați din nou.': 'The information entered is invalid. Check it and try again.',
  'A apărut o eroare neașteptată. Vă rugăm să încercați din nou.': 'An unexpected error occurred. Please try again.',
  'A apărut o eroare. Încearcă din nou.': 'An error occurred. Try again.',
  'Datele introduse nu sunt valide. Verifică formularul.': 'The information entered is invalid. Check the form.',
  'Sesiunea a expirat. Autentifică-te din nou.': 'Your session has expired. Sign in again.',
  'Nu ai permisiunea necesară pentru această acțiune.': 'You do not have permission to perform this action.',
  'Această funcție nu este disponibilă pentru compania ta.': 'This feature is not available for your company.',
  'Sesiunea a expirat. Autentifică-te din nou pentru a continua.': 'Your session has expired. Sign in again to continue.',
  'Conversia valutară nu este disponibilă momentan. Încearcă din nou mai târziu.': 'Currency conversion is currently unavailable. Try again later.',
  'Acest cod este deja folosit. Alege un cod diferit.': 'This code is already in use. Choose a different code.',
  'Tipul de cotație nu mai este disponibil. Actualizează selecția și încearcă din nou.': 'The quotation type is no longer available. Refresh your selection and try again.',
  'Camionul selectat nu mai este disponibil. Actualizează selecția și încearcă din nou.': 'The selected truck is no longer available. Refresh your selection and try again.',
  'Remorca selectată nu mai este disponibilă. Actualizează selecția și încearcă din nou.': 'The selected trailer is no longer available. Refresh your selection and try again.',
  'Șoferul selectat nu mai este disponibil. Actualizează selecția și încearcă din nou.': 'The selected driver is no longer available. Refresh your selection and try again.',
  'Luna selectată s-a modificat. Verifică perioada și încearcă din nou.': 'The selected month has changed. Check the period and try again.',
  'Unele informații nu sunt valide. Verifică valorile marcate și încearcă din nou.': 'Some information is invalid. Check the highlighted values and try again.',
  'Operațiunea a fost oprită înainte de finalizare.': 'The operation was stopped before it was completed.',
  'Conexiunea nu este disponibilă momentan. Verifică internetul și încearcă din nou.': 'The connection is currently unavailable. Check your internet connection and try again.',
  'Informația solicitată nu mai este disponibilă. Reîncarcă pagina și încearcă din nou.': 'The requested information is no longer available. Reload the page and try again.',
  'Datele s-au modificat între timp. Reîncarcă informațiile și încearcă din nou.': 'The information has changed. Reload it and try again.',
  'Au fost trimise prea multe solicitări. Așteaptă puțin și încearcă din nou.': 'Too many requests were sent. Wait a moment and try again.',
  'Serviciul este temporar indisponibil. Încearcă din nou în câteva momente.': 'The service is temporarily unavailable. Try again in a few moments.',
  'Operațiunea nu a putut fi finalizată. Încearcă din nou.': 'The operation could not be completed. Try again.',
  'Vehiculul nu mai există sau nu este disponibil.': 'The vehicle no longer exists or is unavailable.',
  'Necesită reîncercare': 'Retry required',
  'Generală': 'General',
  Da: 'Yes',

  // Documents, signatures, CMR distribution and email notifications
  'Eroare la distribuirea CMR': 'Error distributing the CMR',
  'Vă rugăm să introduceți cel puțin o adresă de email': 'Enter at least one email address',
  'Draft salvat cu succes!': 'Draft saved successfully!',
  'Nu a fost selectat niciun fișier': 'No file has been selected',
  'Doar fișiere PDF sunt permise': 'Only PDF files are allowed',
  'Fișierul este prea mare (max 25 MiB)': 'The file is too large (maximum 25 MiB)',
  'A apărut o problemă la semnături': 'A problem occurred while loading signatures',
  'Nu există semnături înregistrate pentru acest CMR încă.': 'No signatures have been recorded for this CMR yet.',
  'Cu stimă,': 'Kind regards,',
  'data de expirare configurată': 'the configured expiry date',
  'Eroare necunoscută la trimiterea emailului': 'Unknown error while sending the email',
  'Date invalide pentru trimiterea emailului': 'Invalid information for sending the email',
  'Nu aveți autorizația necesară pentru această operațiune': 'You are not authorised to perform this operation',
  'Acces interzis la serviciul de email': 'Access to the email service is forbidden',
  'Prea multe încercări de trimitere email. Încercați din nou mai târziu': 'Too many email attempts. Try again later',
  'Eroare server la trimiterea emailului. Încercați din nou': 'Server error while sending the email. Try again',
  'Eroare de conexiune. Verificați conexiunea la internet': 'Connection error. Check your internet connection',
  'Răspuns invalid de la server. Contactați administratorul': 'Invalid response from the server. Contact the administrator',
  'Nu s-a putut genera PDF-ul. Încercați din nou': 'The PDF could not be generated. Try again',
  'Fișierul PDF este prea mare pentru a fi trimis prin email': 'The PDF is too large to send by email',
  'Nu s-a putut descărca fotografia CMR. Încercați din nou': 'The CMR photo could not be downloaded. Try again',
  'URL-ul fotografiei CMR lipsește': 'The CMR photo URL is missing',
  'Fotografia este prea mare pentru a fi trimisă prin email': 'The photo is too large to send by email',
  'CMR PDF salvat cu succes pe server!': 'CMR PDF saved to the server successfully!',
  'CMR PDF salvat cu succes în sistem!': 'CMR PDF saved in the system successfully!',
  'Eroare la generarea PDF-ului. Vă rugăm să încercați din nou.': 'Error generating the PDF. Please try again.',
  'Eroare la încărcarea datelor': 'Error loading data',
  'Transportul sau documentul CMR nu a fost găsit': 'The transport or CMR document was not found',
  'Nu aveți autorizația pentru a accesa acest transport': 'You are not authorised to access this transport',
  'Eroare server. Încercați din nou mai târziu': 'Server error. Try again later',
  'Sunteți sigur că doriți să ștergeți acest document CMR? Această acțiune nu poate fi anulată.': 'Are you sure you want to delete this CMR document? This action cannot be undone.',
  'Document CMR șters cu succes!': 'CMR document deleted successfully!',
  'Nu există o adresă de email validă pentru expeditor. Te rog să setezi email-ul mai întâi.': 'There is no valid email address for the sender. Set the email address first.',
  'Eroare la generarea codului expeditor': 'Error generating the sender code',
  'Nu aveți autorizația pentru această operațiune': 'You are not authorised to perform this operation',
  'Eroare de conexiune. Verificați internetul': 'Connection error. Check your internet connection',
  'Nu există o adresă de email validă pentru destinatar. Te rog să setezi email-ul mai întâi.': 'There is no valid email address for the consignee. Set the email address first.',
  'Eroare la generarea codului transportator': 'Error generating the carrier code',
  'Nu există fotografie CMR pentru acest transport.': 'There is no CMR photo for this transport.',
  'Nu pot genera PDF-ul CMR pentru semnătură.': 'The CMR PDF for signature could not be generated.',
  'Nu am putut genera PDF-ul pentru semnătură. Încearcă din nou.': 'Could not generate the PDF for signature. Try again.',
  'Cod generat, dar configurarea câmpurilor a eșuat. Toate câmpurile vor fi vizibile.': 'The code was generated, but field configuration failed. All fields will be visible.',
  'Configurarea câmpurilor a fost actualizată': 'Field configuration updated',
  'Cod dezactivat cu succes': 'Code disabled successfully',
  'Eroare la încărcarea documentului CMR': 'Error uploading the CMR document',
  'Eroare la ștergerea documentului CMR': 'Error deleting the CMR document',
  'Document CMR șters cu succes': 'CMR document deleted successfully',
  'Eroare la obținerea documentului CMR': 'Error retrieving the CMR document',
  'Datele trimise pentru semnare nu sunt valide.': 'The information submitted for signature is invalid.',
  'Sesiunea a expirat. Reautentifică-te și încearcă din nou.': 'Your session has expired. Sign in again and retry.',
  'Nu ai permisiunea să trimiți acest document la semnat. Verifică că ești asociat cu transportul.': 'You do not have permission to send this document for signature. Check that you are assigned to the transport.',
  'Documentul sau transportul nu a fost găsit. Reîncarcă pagina și încearcă din nou.': 'The document or transport was not found. Reload the page and try again.',
  'Există deja o cerere de semnare activă pentru acest document.': 'There is already an active signature request for this document.',
  'Fișierul PDF este prea mare (max 25 MiB). Redu dimensiunea documentului și încearcă din nou.': 'The PDF is too large (maximum 25 MiB). Reduce the document size and try again.',
  'eSemneaza a respins cererea. Verifică: telefoane în format +40... , emailuri valide, și că fișierul PDF nu este corupt.': 'eSemneaza rejected the request. Check that phone numbers use the +40... format, email addresses are valid, and the PDF is not corrupted.',
  'eSemneaza a respins datele semnatarilor. Asigură-te că: telefoanele sunt în format internațional (+40712345678), emailurile sunt valide, și numele nu conțin caractere speciale.': 'eSemneaza rejected the signatory details. Ensure phone numbers use international format (+40712345678), email addresses are valid, and names contain no special characters.',
  'eSemneaza nu răspunde. Încearcă din nou în câteva secunde.': 'eSemneaza is not responding. Try again in a few seconds.',
  'Eroare de server la crearea cererii de semnare. Încearcă din nou sau contactează suportul dacă problema persistă.': 'Server error while creating the signature request. Try again or contact support if the problem persists.',
  'Transportul și CMR-ul sunt obligatorii pentru semnarea în puncte fixe.': 'The transport and CMR are required for fixed-position signing.',
  'Upload-ul PDF nu a returnat fileName. Cererea de semnare nu poate fi creată.': 'The PDF upload did not return a file name. The signature request cannot be created.',
  'Nu am putut crea cererea de semnare': 'Could not create the signature request',
  'Nu am putut citi statusul cererii de semnare': 'Could not retrieve the signature request status',
  'Nu am putut încărca cererile de semnare pentru transport': 'Could not load signature requests for the transport',
  'Nu am putut șterge cererea de semnare': 'Could not delete the signature request',
  'Nu am putut descărca documentul semnat': 'Could not download the signed document',
  'Nu am putut salva documentul semnat în storage': 'Could not save the signed document to storage',
  'Nu aveți permisiunea de a semna acest document': 'You do not have permission to sign this document',
  'Documentul nu a fost găsit': 'Document not found',
  'Remorca a fost creată cu succes!': 'Trailer created successfully!',
  'Eroare la ștergerea transportului': 'Error deleting the transport',
  'Transportul a fost creat cu succes, dar documentul CMR nu a putut fi generat automat. Vă rugăm să îl completați manual.': 'The transport was created successfully, but the CMR document could not be generated automatically. Complete it manually.',
  'Transportul a fost creat, dar nu a putut fi adaugat in coada soferului. Verificati atribuirea.': 'The transport was created, but it could not be added to the driver’s queue. Check the assignment.',
  'Șoferul tău are deja un transport între datele selectate.': 'Your driver already has a transport during the selected period.',
  'Resursa selectată nu mai este disponibilă.': 'The selected resource is no longer available.',
  'Nu s-a putut crea transportul. Verifică datele introduse și încearcă din nou.': 'Could not create the transport. Check the information entered and try again.',

  // Domain service/API fallbacks that can surface in the interface
  'Parola nu este destul de sigură. Folosește minimum 8 caractere, litere mari și mici, cifre și un simbol. Evită parole comune sau similare cu datele șoferului.': 'The password is not strong enough. Use at least eight characters, including uppercase and lowercase letters, numbers and a symbol. Avoid common passwords or ones similar to the driver’s details.',
  'Eroare la resetarea parolei': 'Error resetting the password',
  'Nu s-au putut încărca șoferii disponibili.': 'Could not load available drivers.',
  'Nu aveți permisiunea să accesați această pagină': 'You do not have permission to access this page',
  'Eroare de server. Vă rugăm încercați mai târziu': 'Server error. Please try again later',
  'Nu s-au putut încărca datele. Vă rugăm încercați din nou': 'Could not load the data. Please try again',
  'Nu s-a putut crea șoferul. Verifică datele și încearcă din nou.': 'Could not create the driver. Check the information and try again.',
  'Nu există modificări de trimis': 'There are no changes to submit',
  'Nu s-au putut actualiza datele șoferului. Vă rugăm încercați din nou.': 'Could not update the driver details. Please try again.',
  'Doar dispecerii pot reseta parole pentru șoferi': 'Only dispatchers can reset driver passwords',
  'Șofer invalid': 'Invalid driver',
  'Nu aveți permisiunea necesară pentru această acțiune.': 'You do not have the required permission for this action.',
  'Resursa nu a fost găsită.': 'Resource not found.',
  'Nu am putut încărca observațiile pentru transport.': 'Could not load observations for the transport.',
  'Nu am putut încărca observațiile pentru șofer.': 'Could not load observations for the driver.',
  'Nu am putut obține informațiile UIT de la ANAF. Verificați datele introduse și încercați din nou.': 'Could not retrieve UIT information from ANAF. Check the information entered and try again.',
  'Datele trimise către e-Transport sunt invalide. Verificați CIF-ul, numărul de înmatriculare și perioada selectată.': 'The information submitted to e-Transport is invalid. Check the tax ID, registration number and selected period.',
  'Sesiunea a expirat. Reautentificați-vă și încercați din nou.': 'Your session has expired. Sign in again and retry.',
  'Nu aveți permisiunea necesară pentru această acțiune e-Transport.': 'You do not have permission to perform this e-Transport action.',
  'Nu am găsit informații e-Transport pentru datele introduse.': 'No e-Transport information was found for the details entered.',
  'ANAF nu a răspuns în timp util. Încercați din nou în câteva minute.': 'ANAF did not respond in time. Try again in a few minutes.',
  'Serviciul e-Transport este indisponibil momentan. Încercați din nou mai târziu.': 'The e-Transport service is currently unavailable. Try again later.',
  'Nu s-au putut încărca șoferii.': 'Could not load drivers.',
  'Nu s-au putut încărca șoferii Expeditor disponibili.': 'Could not load available freight-forwarding drivers.',
  'Nu s-a putut actualiza transportul Expeditor.': 'Could not update the freight-forwarding transport.',
  'Nu s-a putut crea transportul Expeditor.': 'Could not create the freight-forwarding transport.',
  'Nu s-a putut asigna șoferul.': 'Could not assign the driver.',
  'Nu s-a putut salva șoferul.': 'Could not save the driver.',
  'Nu s-a putut actualiza oprirea Expeditor.': 'Could not update the freight-forwarding stop.',
  'Nu s-a putut replanifica oprirea Expeditor.': 'Could not reschedule the freight-forwarding stop.',
  'Nu s-au putut corecta tipurile opririlor Expeditor.': 'Could not correct the freight-forwarding stop types.',
  'Nu s-a putut încărca istoricul corecțiilor Expeditor.': 'Could not load the freight-forwarding correction history.',
  'Nu am putut încărca feature flags.': 'Could not load feature flags.',
  'Document cu expirare apropiată': 'Document expiring soon',
  'Datele documentului nu sunt valide. Verifică numele, categoria și datele introduse.': 'The document details are invalid. Check the name, category and dates.',
  'Nu ai permisiunea să modifici acest document.': 'You do not have permission to modify this document.',
  'Documentul nu mai există sau nu aparține vehiculului selectat.': 'The document no longer exists or does not belong to the selected vehicle.',
  'Nu am putut salva modificarea. Dacă ai schimbat categoria, verifică dacă acea categorie are deja un document activ. Poți muta documentul într-o categorie liberă sau poți actualiza documentul activ existent.': 'Could not save the change. If you changed the category, check whether that category already has an active document. Move the document to an unused category or update the existing active document.',
  'Date invalide trimise la endpoint.': 'Invalid data was submitted.',
  'Nu aveți permisiunea de a genera foaie de parcurs.': 'You do not have permission to generate a trip sheet.',
  'Nu s-au putut încărca partenerii casei de expediție.': 'Could not load freight-forwarding partners.',
  'Nu s-a putut crea partenerul.': 'Could not create the partner.',
  'Nu s-a putut actualiza partenerul.': 'Could not update the partner.',
  'Nu s-au putut încărca datele comerciale.': 'Could not load the commercial details.',
  'Nu s-au putut actualiza datele comerciale.': 'Could not update the commercial details.',
  'Nu s-a putut crea transportul prin casa de expediție.': 'Could not create the freight-forwarding transport.',
  'Nu s-a putut crea intervenția.': 'Could not create the intervention.',
  'Nu s-a putut actualiza intervenția.': 'Could not update the intervention.',
  'Întreținere Preventivă': 'Preventive Maintenance',
  'Reparație': 'Repair',
  'Revizie Periodică': 'Periodic Service',
  'În Lucru': 'In Progress',
  'Nu am putut încărca lista de șoferi din Optifleet.': 'Could not load the driver list from Optifleet.',
  'Parametrii driverId, fromDatetime și toDatetime sunt obligatorii.': 'The driverId, fromDatetime and toDatetime parameters are required.',
  'Nu am putut încărca sumarul șoferului din Optifleet.': 'Could not load the driver summary from Optifleet.',
  'Nu am putut încărca activitatea șoferului din Optifleet.': 'Could not load the driver activity from Optifleet.',
  'Parametrii fromDate și toDate trebuie să fie în format YYYY-MM-DD.': 'The fromDate and toDate parameters must use the YYYY-MM-DD format.',
  'Parolă actuală incorectă': 'Incorrect current password',
  'Parolă nouă invalidă': 'Invalid new password',
  'Eroare de validare': 'Validation error',
  'Eroare la schimbarea parolei': 'Error changing the password',
  'Nu am putut procesa cererea de profitabilitate.': 'Could not process the profitability request.',
  'Lista componentelor s-a modificat. Reîncarcă și încearcă din nou.': 'The component list has changed. Reload it and try again.',
  'Nu am putut adăuga componenta de grupaj. Verifică datele și încearcă din nou.': 'Could not add the consolidated-load item. Check the information and try again.',
  'Nu am putut actualiza componenta de grupaj. Verifică datele și încearcă din nou.': 'Could not update the consolidated-load item. Check the information and try again.',
  'Transportul nu a fost găsit.': 'Transport not found.',
  'Nu aveți permisiunea de a accesa documentele acestui transport.': 'You do not have permission to access this transport’s documents.',
  'Eroare la încărcarea documentelor. Vă rugăm încercați din nou.': 'Error loading the documents. Please try again.',
  'Document, titlu și categorie sunt obligatorii pentru încărcare.': 'A document, title and category are required for upload.',
  'Nu aveți permisiunea de a adăuga documente la acest transport.': 'You do not have permission to add documents to this transport.',
  'Date invalide. Verificați câmpurile și încercați din nou.': 'Invalid data. Check the fields and try again.',
  'Eroare la încărcarea documentului. Vă rugăm încercați din nou.': 'Error uploading the document. Please try again.',
  'Documentul nu a fost găsit.': 'Document not found.',
  'Nu aveți permisiunea de a modifica acest document.': 'You do not have permission to modify this document.',
  'Eroare la actualizarea documentului. Vă rugăm încercați din nou.': 'Error updating the document. Please try again.',
  'Nu aveți permisiunea de a șterge acest document.': 'You do not have permission to delete this document.',
  'Eroare la ștergerea documentului. Vă rugăm încercați din nou.': 'Error deleting the document. Please try again.',
  'Nu am putut procesa cererea de cotație.': 'Could not process the quotation request.',
  'Nu am putut încărca expedițiile active.': 'Could not load active shipments.',
  'Nu am putut încărca expedițiile din luna curentă pe șofer.': 'Could not load this month’s shipments by driver.',
  'Nu am putut încărca expedițiile din luna curentă pentru șofer.': 'Could not load this month’s shipments for the driver.',
  'Nu am putut încărca detaliile expediției.': 'Could not load the shipment details.',
  'Nu am putut încărca detaliile șoferului.': 'Could not load the driver details.',
  'Nu am putut încărca lista șoferilor blocați.': 'Could not load the list of blocked drivers.',
  'Nu am putut încărca lista șoferilor disponibili.': 'Could not load the list of available drivers.',
  'Nu am putut încărca statusul SSE.': 'Could not load the SSE status.',
  'Nu s-au putut încărca șoferii': 'Could not load drivers',
  'Nu s-au putut încărca camioanele': 'Could not load trucks',
  'Nu s-au putut încărca remorcile': 'Could not load trailers',
  'Nu s-au putut încărca datele necesare. Vă rugăm să reîncărcați pagina sau să contactați administratorul.': 'Could not load the required data. Reload the page or contact the administrator.',
  'Selecteaza sofer': 'Select a driver',
  'Nu există puncte de livrare pentru optimizare.': 'There are no delivery points to optimise.',
  'Nu s-a găsit token de autentificare valid': 'No valid authentication token was found',
  'Token de autentificare lipsește sau este invalid': 'The authentication token is missing or invalid',
  'Flota de vehicule lipsește sau este goală': 'The vehicle fleet is missing or empty',
  'Lista de comenzi lipsește sau este goală': 'The order list is missing or empty',
  'Token de autentificare invalid sau expirat': 'The authentication token is invalid or expired',
  'Nu aveți permisiunea pentru această operațiune': 'You do not have permission to perform this operation',
  'Eroare de server - încercați din nou mai târziu': 'Server error — try again later',
  'Optimizarea a eșuat': 'Optimisation failed',
  'Cel puțin două puncte de trecere sunt necesare pentru calcularea rutei': 'At least two waypoints are required to calculate the route',
  'Token de autentificare lipsește': 'The authentication token is missing',
  'Ruta nu a fost găsită sau a expirat': 'The route was not found or has expired',
  'Datele de optimizare sunt invalide': 'The optimisation data is invalid',
  'Nu s-a primit ID-ul de optimizare de la server': 'The server did not return an optimisation ID',
  'Serviciul nu este disponibil pentru verificarea statusului': 'The service is unavailable for status checks',
  'Datele de rutare sunt invalide': 'The routing data is invalid',
  'Google Maps Directions Service nu este disponibil.': 'The Google Maps Directions Service is unavailable.',
  'Acces interzis la datele transportului': 'Access to the transport data is forbidden',
  'Date invalide pentru generarea codului': 'Invalid data for generating the code',
  'Acces interzis la generarea codurilor': 'Access to code generation is forbidden',

  // PTV route-planning chrome not yet migrated to semantic i18next keys
  'Trage pentru a reordona': 'Drag to reorder',
  Ridicare: 'Pickup',
  '✅ Punct salvat cu succes!': '✅ Point saved successfully!',
  'Timp max lucru (minute)': 'Maximum working time (minutes)',
  'PICKUP - Ridicare': 'PICKUP - Pickup',
  'Timp Total': 'Total Time',
  Opriri: 'Stops',
  'Timp servire:': 'Service time:',
  'Optimizarea s-a completat cu succes': 'Optimisation completed successfully',
  'Descriere:': 'Description:',
  'Mesaj:': 'Message:',
  'Mod Rutare': 'Routing Mode',
  Trafic: 'Traffic',
  'Trafic mediu': 'Average traffic',
  'Dolar american (USD)': 'US dollar (USD)',
  'Forint maghiar (HUF)': 'Hungarian forint (HUF)',
  'Zlot polonez (PLN)': 'Polish zloty (PLN)',
  'Emisii CO2': 'CO₂ emissions',
  'Evenimente trafic': 'Traffic events',
  'Raport program': 'Schedule report',
  'Calcularea emisiilor se va face conform standardului ISO 14083:2023': 'Emissions will be calculated in accordance with ISO 14083:2023',
  '15 minute': '15 minutes',
  '20 minute': '20 minutes',
  '30 minute': '30 minutes',
  '45 minute (Standard EU)': '45 minutes (EU standard)',
  'complet blocate': 'completely blocked',
  'Punct de ridicare': 'Pickup point',
  'Lungime (cm)': 'Length (cm)',
  'Motor & Combustibil': 'Engine & Fuel',
  'Standard Emisii': 'Emissions Standard',
  'Materiale Periculoase': 'Hazardous Materials',

  // Shared configuration labels and model validation
  'Echipaj transport': 'Transport crew',
  'Management flotă': 'Fleet management',
  'Planificare rută': 'Route planning',
  'Urmărire live': 'Live tracking',
  'Țară Expeditor': 'Sender country',
  'Nume Expeditor': 'Sender name',
  'Destinatar': 'Consignee',
  'Nume Destinatar': 'Consignee name',
  'Țară Destinatar': 'Consignee country',
  'Metri cubi': 'Cubic metres',
  'Locații': 'Locations',
  'Dată încărcare': 'Loading date',
  'Dată livrare': 'Delivery date',
  'ID Transport': 'Transport ID',
  'Execuție': 'Execution',
  'Informații Camion': 'Truck information',
  'Informații Remorcă': 'Trailer information',
  'Estimare întârziere': 'Delay estimate',
  'Bucăți': 'Pieces',
  'Metri pătrați': 'Square metres',
  'Seria trebuie să aibă cel puțin 2 caractere': 'The series must be at least two characters long',
  'Numărul este obligatoriu': 'The number is required',
  'Denumirea trebuie să aibă cel puțin 3 caractere': 'The name must be at least three characters long',
  'CIF invalid': 'Invalid tax ID',
  'Trebuie să adăugați cel puțin un produs': 'Add at least one product',
  'Valoarea nu poate fi negativă': 'The value cannot be negative',
  'Format oră invalid (HH:MM:SS)': 'Invalid time format (HH:MM:SS)',
  'Șofer inactiv': 'Inactive driver',
  'Userul nu mai are rol de șofer': 'The user no longer has the driver role',
  'Șofer în concediu': 'Driver on leave',
  'Vehicul în service': 'Vehicle undergoing service',
  'Există transporturi nefinalizate': 'There are unfinished transports',
  'Vehiculul are deja o intervenție activă': 'The vehicle already has an active intervention',
  'Transportul are mai mulți șoferi activi': 'The transport has multiple active drivers',
  'motiv necunoscut': 'unknown reason',
  'În derulare': 'In progress',
  'Aprobat': 'Approved',
  'Informație': 'Information',
  'Expirare document': 'Document expiry',
  'Schimbare status șofer': 'Driver status change',
  'Actualizare transport': 'Transport update',
  'Alertă sistem': 'System alert',
  'Cereri de concediu': 'Leave requests',
  'Șofer & Dispecer': 'Driver & Dispatcher',
  'În pauză': 'On break',
  'Nu': 'No',
  'Terminată': 'Completed',
  'Frigorifică': 'Refrigerated',
  'Furgon (închisă)': 'Dry van (enclosed)',
  'Cisternă': 'Tanker',
  'Cu prelată': 'Curtainsider',
  'Platformă joasă': 'Low-loader',
  'Platformă cu treaptă': 'Step-deck',
  'Număr Înmatriculare': 'Registration number',
  'Număr VIN': 'VIN',
  'An Fabricație': 'Year of manufacture',
  'Tip Remorcă': 'Trailer type',
  'VIN-ul trebuie să aibă exact 17 caractere': 'The VIN must be exactly 17 characters long',
  'Selectează modul Solo sau Echipaj': 'Select Solo or Crew mode',
  'Același șofer nu poate ocupa ambele poziții': 'The same driver cannot occupy both positions',
  'Selectează dacă transportul este național sau internațional': 'Select whether the transport is domestic or international',
  'Prețul trebuie să fie un număr pozitiv': 'The price must be a positive number',
  'Moneda trebuie să fie un cod ISO de 3 litere': 'The currency must be a three-letter ISO code',
  'Numărul remorcii externe este obligatoriu': 'The external trailer registration number is required',
  'Adaugă cel puțin o vamă pentru transportul internațional.': 'Add at least one customs point for an international transport.',
  'Completează țara de plecare și țara de destinație pentru fiecare vamă.': 'Enter the departure and destination countries for each customs point.',
  'Adaugă cel puțin o oprire de încărcare și una de descărcare.': 'Add at least one loading stop and one unloading stop.',
  'Transportul trebuie să aibă cel puțin o încărcare și o descărcare.': 'The transport must have at least one loading stop and one unloading stop.',
  'Data și ora planificată sunt obligatorii pentru fiecare oprire.': 'The scheduled date and time are required for every stop.',
  'Codul AROBS trebuie să fie un număr întreg pozitiv': 'The AROBS code must be a positive integer',
  'Vă rugăm introduceți prenumele': 'Enter the first name',
  'Vă rugăm introduceți numărul de telefon': 'Enter the phone number',
  'Vă rugăm introduceți cel puțin o cifră pentru numărul de telefon': 'Enter at least one digit for the phone number',
  'Vă rugăm introduceți o adresă de email validă': 'Enter a valid email address',
  'Vă rugăm introduceți data angajării': 'Enter the employment date',
  'Selecteaza Remorca': 'Select a trailer',
  'număr de telefon': 'phone number',
  'data nașterii': 'date of birth',
  'data angajării': 'employment date',
  'setarea ADR': 'ADR setting',
  'setarea transport frigorific': 'refrigerated-transport setting',
  'salariu profitabilitate': 'profitability salary',

  // Application-owned PDF and export labels
  'Raport lunar concedii': 'Monthly leave report',
  'Raport anual concedii': 'Annual leave report',
  '1. Situatia generala': '1. Overview',
  '1. Situatia generala anuala': '1. Annual overview',
  'Indicator': 'Metric',
  'Total soferi': 'Total drivers',
  'Total soferi in sistem': 'Total drivers in the system',
  'Soferi in concediu': 'Drivers on leave',
  'Rata utilizare': 'Leave rate',
  'Total zile concediu': 'Total leave days',
  'Media zile/sofer': 'Average days per driver',
  '2. Soferi in concediu': '2. Drivers on leave',
  'Nu exista soferi in concediu': 'There are no drivers on leave',
  'Nume Sofer': 'Driver name',
  'Zile': 'Days',
  '3. Concluzii': '3. Conclusions',
  'Niciun sofer in concediu in aceasta perioada.': 'No drivers were on leave during this period.',
  'Atentie: Monitorizati acoperirea rutelor.': 'Attention: monitor route coverage.',
  'Document confidential': 'Confidential document',
  'CERERE DE ACORDARE': 'LEAVE REQUEST',
  'CONCEDIU DE ODIHNA': 'ANNUAL LEAVE',
  'Va multumesc anticipat.': 'Thank you in advance.',
  'Semnatura:': 'Signature:',
  'Total cereri trimise': 'Total requests submitted',
  'Cereri aprobate': 'Approved requests',
  'Cereri respinse': 'Rejected requests',
  'Cereri in asteptare': 'Pending requests',
  'Rata de aprobare': 'Approval rate',
  'Luna cu cele mai multe concedii': 'Month with the most leave',
  'Luna cu cele mai putine concedii': 'Month with the least leave',
  '2. Soferi cu concedii in anul curent': '2. Drivers with leave this year',
  'Nu exista soferi cu concedii in acest an': 'There are no drivers with leave this year',
  'Total Zile': 'Total days',
  'An': 'Year',
  '3. Concluzii anuale HR': '3. Annual HR conclusions',
  'Observatie: Rata mare de aprobare indica planificare eficienta.': 'Observation: the high approval rate indicates effective planning.',
  'Recomandare: Analiza cauzelor pentru rata scazuta de aprobare.': 'Recommendation: analyse the reasons for the low approval rate.',
  'Forma juridica': 'Legal form',
  'Nr inmatr. Reg. Com./an:': 'Trade Register no./year:',
  'Capital social': 'Share capital',
  'Sediul:': 'Registered office:',
  'Cumparator:': 'Buyer:',
  'Nr. inmatr. Reg. Com.:': 'Trade Register no.:',
  'Punct de lucru': 'Business location',
  'Judetul:': 'County:',
  'Adresa punct de lucru': 'Business location address',
  'Contul:': 'Account:',
  'Contul': 'Account',
  'Banca:': 'Bank:',
  'Tel/Fax': 'Tel/Fax',
  'AVIZ DE INSOTIRE A MARFII': 'DELIVERY NOTE',
  'AVIZ DE INSOTIRE A MARFII (Continuare)': 'DELIVERY NOTE (CONTINUED)',
  'AVIZ DE INSOTIRE A MARFII (Semnaturi)': 'DELIVERY NOTE (SIGNATURES)',
  'N r .': 'No.',
  'Crt.': 'Item',
  'SPECIFICATIA': 'DESCRIPTION',
  '(produse, ambalaje, etc.)': '(products, packaging, etc.)',
  'U.M.': 'Unit',
  'Cantitatea': 'Quantity',
  'livrata': 'delivered',
  'Pretul unitar': 'Unit price',
  '(fara T.V.A.) – lei -': '(excluding VAT) — RON',
  '-lei-': '-RON-',
  'Date privind privind expeditia:': 'Shipment details:',
  'Semnatura': 'Signature',
  'si stampila': 'and stamp',
  'furnizorului': 'of the supplier',
  'Numele delegatului': 'Representative name',
  'Expedierea s-a facut in prezenta noastra la': 'The shipment was dispatched in our presence on',
  'Semnaturile....................................': 'Signatures....................................',
  'de primire': 'on receipt',
  'Aviz de Insotire a Marfii': 'Delivery Note',
  'aviz, transport, marfa': 'delivery note, transport, goods',
  'Document Oficial': 'Official Document',
  'DOCUMENT OFICIAL': 'OFFICIAL DOCUMENT',
  'transport, document, oficial': 'transport, document, official',
  'Document generat automat': 'Automatically generated document',
  'Intocmit de:': 'Prepared by:',
  '(Nume, prenume, semnatura)': '(Surname, first name, signature)',
  'Primit de:': 'Received by:',
});

// Dynamic runtime strings are deliberately narrow: every rule is anchored to
// copy owned by this application, and every captured value is returned without
// translation so business data cannot be altered.
export const legacyEnglishSystemPatterns = Object.freeze([
  // Live Tracking counters
  [/^Soferi pe harta \((\d+)\)$/u, 'Drivers on the map ($1)'],
  [/^(\d+) activi • (\d+) inactivi$/u, '$1 active • $2 inactive'],

  // Hooks and notification messages
  [/^CMR distribuit cu succes către (\d+) destinație$/u, 'CMR distributed successfully to $1 destination'],
  [/^CMR distribuit cu succes către (\d+) destinații$/u, 'CMR distributed successfully to $1 destinations'],
  [/^CMR distribuit parțial: (\d+)\/(\d+) reușite$/u, 'CMR partially distributed: $1/$2 successful'],
  [/^Eroare la distribuirea CMR: (.+)$/u, 'Error distributing the CMR: $1'],
  [/^Transport CMR (actualizat|creat) cu succes!$/u, (_, action) => `CMR transport ${action === 'actualizat' ? 'updated' : 'created'} successfully!`],
  [/^Șoferul "([\s\S]+)" a fost șters cu succes\.$/u, 'Driver "$1" deleted successfully.'],
  [/^Eroare la ștergerea șoferului: (.+)$/u, 'Error deleting the driver: $1'],
  [/^Șofer adăugat cu succes\s+Nume: ([\s\S]+)\s+Parolă: ([\s\S]+)\s+Vă rugăm să salvați aceste informații într-un loc sigur\.$/u, 'Driver added successfully\n\nName: $1\nPassword: $2\n\nStore these credentials securely.'],
  [/^Maxim (\d+) fișiere sunt permise\.$/u, 'A maximum of $1 files is allowed.'],
  [/^Fișierul "([\s\S]+)" depășește limita de (\d+(?:[.,]\d+)?)MB\.$/u, 'File "$1" exceeds the $2 MB limit.'],
  [/^Tipul de fișier pentru "([\s\S]+)" nu este suportat\.$/u, 'The file type for "$1" is not supported.'],
  [/^Prea multe încercări\. Reîncearcă în (.+)$/u, 'Too many attempts. Try again in $1'],
  [/^Eroare la încărcarea Google Maps: (.+)\. Verificați conexiunea la internet și API key-ul\.$/u, 'Error loading Google Maps: $1. Check your internet connection and API key.'],
  [/^Eroare la salvarea PDF-ului pe server: (.+)$/u, 'Error saving the PDF to the server: $1'],
  [/^PTV Optimization Service nu poate fi inițializat: (.+)$/u, 'The PTV Optimisation Service could not be initialised: $1'],
  [/^Eroare la verificarea statusului: (.+)$/u, 'Error checking the status: $1'],
  [/^Eroare la calcularea rutei: (.+)$/u, 'Error calculating the route: $1'],
  [/^Eroare la ștergerea CMR-ului: (.+)$/u, 'Error deleting the CMR: $1'],
  [/^Eroare la salvarea email-ului: (.+)$/u, 'Error saving the email address: $1'],
  [/^Email (expeditor|destinatar) salvat cu succes!$/u, (_, recipient) => `The ${translateRecipient(recipient)} email address was saved successfully!`],
  [/^Eroare la generarea codului expeditor: (.+)$/u, 'Error generating the sender code: $1'],
  [/^Eroare la generarea codului transportator: (.+)$/u, 'Error generating the carrier code: $1'],
  [/^Eroare la trimiterea codului pentru (.+)$/u, 'Error sending the code for $1'],
  [/^Cod de acces generat pentru (Expeditor|Destinatar)(!?)$/u,
    (_, recipient, punctuation) => `Access code generated for the ${translateRecipient(recipient).toLowerCase()}${punctuation}`],
  [/^• Rol: (.+)$/u, (_, role) => `• Role: ${translateRecipient(role)}`],
  [/^Eroare la generarea codului: (.+)$/u, 'Error generating the code: $1'],
  [/^Eroare la actualizarea configurării: (.+)$/u, 'Error updating the configuration: $1'],
  [/^Eroare la dezactivarea codului: (.+)$/u, 'Error deactivating the code: $1'],
  [/^Eroare la listarea codurilor: (.+)$/u, 'Error listing the codes: $1'],
  [/^Eroare: (.+)$/u, 'Error: $1'],
  [/^Eroare la crearea remorcii: (.+)$/u, 'Error creating the trailer: $1'],
  [/^Eroare la încărcarea documentelor: (.+)$/u, 'Error loading the documents: $1'],
  [/^Eroare la obținerea fotografiei CMR: (.+)$/u, 'Error retrieving the CMR photo: $1'],
  [/^Eroare la obținerea fotografiilor CMR: (.+)$/u, 'Error retrieving the CMR photos: $1'],

  // Email subjects, confirmations and errors
  [/^Cod de acces CMR Transport #(\S+) - (Expeditor|Destinatar)$/u, (_, id, recipient) => `CMR access code — Transport #${id} — ${translateRecipient(recipient)}`],
  [/^CMR Transport #(\S+) - Document pentru (Expeditor|Destinatar)$/u, (_, id, recipient) => `CMR Transport #${id} — Document for ${translateRecipient(recipient)}`],
  [/^Fotografie CMR Transport #(\S+) - Document pentru (Expeditor|Destinatar)$/u, (_, id, recipient) => `CMR photo — Transport #${id} — Document for ${translateRecipient(recipient)}`],
  [/^Cod de acces trimis cu succes către (expeditor|destinatar)!$/u, (_, recipient) => `Access code sent successfully to the ${translateRecipient(recipient)}!`],
  [/^Email trimis cu succes către (expeditor|destinatar)!$/u, (_, recipient) => `Email sent successfully to the ${translateRecipient(recipient)}!`],
  [/^Fotografie CMR trimisă cu succes către (expeditor|destinatar)!$/u, (_, recipient) => `CMR photo sent successfully to the ${translateRecipient(recipient)}!`],
  [/^Eroare la trimiterea codului către (expeditor|destinatar): (.+)$/u, (_, recipient, error) => `Error sending the code to the ${translateRecipient(recipient)}: ${error}`],
  [/^Eroare la trimiterea email-ului către (expeditor|destinatar): (.+)$/u, (_, recipient, error) => `Error sending the email to the ${translateRecipient(recipient)}: ${error}`],
  [/^Eroare la trimiterea fotografiei CMR către (expeditor|destinatar): (.+)$/u, (_, recipient, error) => `Error sending the CMR photo to the ${translateRecipient(recipient)}: ${error}`],
  [/^Email-ul pentru (expeditor|destinatar) nu este valid\. Vă rugăm să introduceți o adresă de email validă\.$/u, (_, recipient) => `The email address for the ${translateRecipient(recipient)} is invalid. Enter a valid email address.`],
  [/^(?:📋 DOCUMENT CMR|📷 FOTOGRAFIE CMR) - Transport #[\s\S]+$/u, translateGeneratedCmrEmail],

  // API/service errors with status or safe identifiers
  [/^Eroare la resetarea parolei \(Status: (\d+)\)$/u, 'Error resetting the password (status: $1)'],
  [/^Cod incorect\. (\d+) încercări rămase\.$/u, 'Incorrect code. $1 attempts remaining.'],
  [/^Nu s-a putut actualiza salariul \((\d+)\)\.?$/u, 'Could not update the salary ($1).'],
  [/^Generarea foii de parcurs a eșuat \(status (\d+)\)$/u, 'Trip-sheet generation failed (status $1)'],
  [/^Nu s-a putut crea cererea \((\d+)\)\.?$/u, 'Could not create the request ($1).'],
  [/^Nu s-a putut aproba cererea \((\d+)\)\.?$/u, 'Could not approve the request ($1).'],
  [/^Eroare la schimbarea parolei \(Status: (\d+)\)$/u, 'Error changing the password (status: $1)'],
  [/^Eroare la semnarea documentului: (.+)$/u, 'Error signing the document: $1'],
  [/^Eroare la verificarea codului: (.+)$/u, 'Error verifying the code: $1'],
  [/^Eroare la actualizarea CMR: (.+)$/u, 'Error updating the CMR: $1'],
  [/^Cererea pentru planificarea expedițiilor a eșuat \((\d+)\)\.?$/u, 'The shipment-planning request failed ($1)'],
  [/^Nu s-a putut (crea transportul|actualiza transportul|actualiza echipajul|încărca istoricul echipajului|încărca transportul|asigna transporturile|porni transportul|adăuga transportul în coadă|adăuga oprirea|replanifica oprirea|corecta tipurile opririlor|încărca istoricul corecțiilor|actualiza oprirea) \((\d+)\)$/u,
    (_, action, status) => {
      const actions = {
        'crea transportul': 'create the transport',
        'actualiza transportul': 'update the transport',
        'actualiza echipajul': 'update the crew',
        'încărca istoricul echipajului': 'load the crew history',
        'încărca transportul': 'load the transport',
        'asigna transporturile': 'assign the transports',
        'porni transportul': 'start the transport',
        'adăuga transportul în coadă': 'add the transport to the queue',
        'adăuga oprirea': 'add the stop',
        'replanifica oprirea': 'reschedule the stop',
        'corecta tipurile opririlor': 'correct the stop types',
        'încărca istoricul corecțiilor': 'load the correction history',
        'actualiza oprirea': 'update the stop',
      };
      return `Could not ${actions[action]} (${status})`;
    }],
  [/^Nu am putut (programa sincronizarea AROBS|încărca sincronizările lunare AROBS|programa sincronizarea lunară AROBS) \((\d+)\)$/u, (_, action, status) => {
    const actions = {
      'programa sincronizarea AROBS': 'schedule the AROBS synchronisation',
      'încărca sincronizările lunare AROBS': 'load the monthly AROBS synchronisations',
      'programa sincronizarea lunară AROBS': 'schedule the monthly AROBS synchronisation',
    };
    return `Could not ${actions[action]} (${status})`;
  }],
  [/^Nu s-a putut actualiza (camionul|remorca) \((\d+)\)$/u, (_, vehicle, status) => `Could not update the ${vehicle === 'camionul' ? 'truck' : 'trailer'} (${status})`],
  [/^Nu s-au putut actualiza costurile \((\d+)\)$/u, 'Could not update the costs ($1)'],
  [/^Nu s-a putut actualiza asocierea TrackGPS \((\d+)\)$/u, 'Could not update the TrackGPS link ($1)'],
  [/^Nu am putut (încărca documentele camionului|încărca documentul camionului|actualiza documentul camionului|șterge documentul camionului|încărca documentele remorcii|încărca documentul remorcii|actualiza documentul remorcii|șterge documentul remorcii|încărca documentele vehiculului|încărca documentul vehiculului|actualiza documentul vehiculului|șterge documentul vehiculului|verifica documentele care expiră|verifica data expirării documentului)\. Încearcă din nou\.$/u,
    (_, action) => {
      const actions = {
        'încărca documentele camionului': 'load the truck documents',
        'încărca documentul camionului': 'upload the truck document',
        'actualiza documentul camionului': 'update the truck document',
        'șterge documentul camionului': 'delete the truck document',
        'încărca documentele remorcii': 'load the trailer documents',
        'încărca documentul remorcii': 'upload the trailer document',
        'actualiza documentul remorcii': 'update the trailer document',
        'șterge documentul remorcii': 'delete the trailer document',
        'încărca documentele vehiculului': 'load the vehicle documents',
        'încărca documentul vehiculului': 'upload the vehicle document',
        'actualiza documentul vehiculului': 'update the vehicle document',
        'șterge documentul vehiculului': 'delete the vehicle document',
        'verifica documentele care expiră': 'check expiring documents',
        'verifica data expirării documentului': 'check the document expiry date',
      };
      return `Could not ${actions[action]}. Try again.`;
    }],
  [/^Cererea a eșuat \((\d+)\)$/u, 'The request failed ($1)'],
  [/^Resursă indisponibilă \((.+)\)$/u, (_, reason) => `Resource unavailable (${reason === 'motiv necunoscut' ? 'unknown reason' : reason})`],
  [/^Șofer în concediu: (.+)$/u, 'Driver on leave: $1'],
  [/^Există transporturi nefinalizate \((.+)\)$/u, (_, details) => `There are unfinished transports (${details.replace(/^transporturi:/u, 'transports:')})`],
  [/^Transportul are mai mulți șoferi activi: (.+)$/u, 'The transport has multiple active drivers: $1'],
  [/^(număr de telefon|data nașterii|data angajării|setarea ADR|setarea transport frigorific|salariu profitabilitate): (.+)$/u,
    (_, field, detail) => `${({
      'număr de telefon': 'Phone number',
      'data nașterii': 'Date of birth',
      'data angajării': 'Employment date',
      'setarea ADR': 'ADR setting',
      'setarea transport frigorific': 'Refrigerated-transport setting',
      'salariu profitabilitate': 'Profitability salary',
    })[field]}: ${detail}`],
  [/^Feature-ul (.+) nu este activ pentru compania ta\.$/u, (_, feature) => `The ${translateFeatureLabel(feature)} feature is not enabled for your company.`],

  // Validation with product positions or generated field lists
  [/^Vă rugăm să completați următoarele câmpuri obligatorii \(\*\): (.+)$/u, (_, labels) => `Complete the following required fields (*): ${translateGeneratedFieldList(labels)}`],
  [/^Vă rugăm completați următoarele câmpuri: (.+)$/u, (_, labels) => `Complete the following fields: ${translateGeneratedFieldList(labels)}`],
  [/^Denumirea produsului (\d+) este obligatorie$/u, 'The name of product $1 is required'],
  [/^Cantitatea pentru produsul (\d+) nu poate fi negativă$/u, 'The quantity for product $1 cannot be negative'],
  [/^Prețul pentru produsul (\d+) nu poate fi negativ$/u, 'The price for product $1 cannot be negative'],

  // Generated leave reports and forms; names, dates and figures are preserved.
  [/^Raport lunar concedii – (.+)$/u, 'Monthly leave report — $1'],
  [/^Raport anual concedii – (\d{4})$/u, 'Annual leave report — $1'],
  [/^Data: (.+)$/u, 'Date: $1'],
  [/^Pag\. (\d+)$/u, 'Page $1'],
  [/^(\d+) soferi in concediu din (\d+) total \((.+)%\)\.$/u, '$1 drivers on leave out of $2 ($3%).'],
  [/^Total zile concediu: (\d+) zile\.$/u, 'Total leave: $1 days.'],
  [/^Media: (.+) zile\/sofer\.$/u, 'Average: $1 days per driver.'],
  [/^Subsemnatul ([\s\S]+), angajat al societatii ([\s\S]+), in calitate de ([\s\S]+), prin prezenta va rog sa-mi aprobati efectuarea concediului de odihna incepand cu data de ([^ ]+) pana in data de ([^ ]+) inclusiv, adica ([^ ]+) zile\.$/u,
    (_, employee, company, role, start, end, days) => `I, ${employee}, an employee of ${company} in the role of ${role === 'sofer' ? 'driver' : role}, hereby request approval for annual leave from ${start} through ${end} inclusive, totalling ${days} days.`],
  [/^In anul (\d{4}), au fost trimise (\d+) cereri de concediu, din care (\d+) au fost aprobate\.$/u, 'In $1, $2 leave requests were submitted, of which $3 were approved.'],
  [/^Rata de aprobare: (.+)% din total cereri\.$/u, 'Approval rate: $1% of all requests.'],
  [/^Total zile de concediu utilizate: (\d+) zile\.$/u, 'Total leave taken: $1 days.'],
  [/^Media zilelor de concediu per sofer: (.+) zile\.$/u, 'Average leave per driver: $1 days.'],
  [/^Perioada de varf pentru concedii: (.+)\.$/u, 'Peak leave period: $1.'],
  // Delivery-note PDF shell; document values remain untouched.
  [/^Seria (.*?)\s+Nr\. (.+)$/u, 'Series $1    No. $2'],
  [/^Data \(ziua, luna, anul\): (.+)$/u, 'Date (day, month, year): $1'],
  [/^B\.I\.\/C\.I\. Seria (.*?) Nr\. (.*)$/u, 'Identity document Series $1 No. $2'],
  [/^Mijlocul de transport: (.*)$/u, 'Means of transport: $1'],
  [/^data de (.*?) ora (.*)$/u, 'date $1 time $2'],
  [/^Aviz (.*?) (.*)$/u, 'Delivery Note $1 $2'],
  [/^Poziție de semnătură CMR necunoscută: (.+)$/u, 'Unknown CMR signature position: $1'],
]);
